import {
  Box,
  Button,
  FloatingIndicator,
  Group,
  Paper,
  Radio,
  Stack,
  Tabs,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconDeviceFloppy, IconLink, IconPencil, IconUpload } from '@tabler/icons-react'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useRef, useState } from 'react'

import { AIInputWrapper } from '@/components/AIInputWrapper'
import { allUploadMimeTypes, FileUpload, rejectedFileUploadText } from '@/components/FileUpload'
import { InputLabel } from '@/components/InputLabel'
import { theme } from '@/configs/theme'
import { useAIFieldGeneration } from '@/hooks/useAIFieldGeneration'
import { useGlobalState } from '@/hooks/useGlobalState'
import { AddStepDataContentModal } from '@/screens/ContentModuleFormScreen/AddStepDataContentModal'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import { FAQSection } from '@/screens/ContentModuleFormScreen/FAQSection'
import { ResourcesTable } from '@/screens/ContentModuleFormScreen/ResourcesTable'
import { UploadedFilePreview } from '@/screens/ContentModuleFormScreen/UploadedFilePreview'
import { useStepModalState } from '@/screens/ContentModuleFormScreen/useStepModalState'
import * as commonClasses from '@/styles/componentCommons.css'
import * as classes from '@/styles/ContentModuleFormScreen.css'
import { castStepToZodForm, castZodFormToStep } from '@/utils/contentModuleForm'
// import { getRequiredFieldDescriptionProps } from '@/utils/forms'

type ContentTabValue = 'upload' | 'website' | 'manual'

export const StepModal = observer(function StepModal(props: CMFormSection) {
  const { uiStore } = useGlobalState()
  const [contentTab, setContentTab] = useState<ContentTabValue>('upload')
  const tabInitializedRef = useRef(false)
  const [tabsRootRef, setTabsRootRef] = useState<HTMLDivElement | null>(null)
  const [tabControlsRefs, setTabControlsRefs] = useState<Record<string, HTMLButtonElement | null>>({})
  const [descriptionError, setDescriptionError] = useState<string | null>(null)

  const {
    // Queries
    stepResources,

    // Website extraction
    isExtracting,
    websiteUrl,
    setWebsiteUrl,
    handleExtractFromWebsite,

    // Pending changes
    pendingFileName,
    setPendingFileName,
    showInApp,
    setShowInApp,
    pendingDescription,
    setPendingDescription,

    // Description AI
    descriptionAIState,
    descriptionSuggestion,
    generateSourceFileDescription,
    generateSourceFileDescriptionWithGuide,
    acceptDescriptionSuggestion,
    rejectDescriptionSuggestion,

    // Upload phase tracking
    uploadPhase,
    uploadProgress,
    processProgressPct,

    // File upload
    cancelContentUpload,
    processDroppedFiles,

    // Step creation
    isStepCreating,
    cancelStepCreation,

    // Refs
    autoGenerationTriggeredRef,
    descriptionAutoGenTriggeredRef,
    faqSectionRef,

    // Derived state
    hasContent,
    isMutating,
    contentLoading,
    uploadedFileResource,
    sourceFileResource,
  } = useStepModalState(props)

  const { getFieldState, generateFieldByName, regenerateFieldByName, acceptStepSuggestion, rejectSuggestion } =
    useAIFieldGeneration(props)

  const openDataContentModal = useCallback(() => {
    props.stepForm.setFieldValue('content.text', props.stepContentQuery.data?.content ?? '')

    props.openModal?.({
      title: `Edit File Content Manually`,
      subtitle: 'Copy and paste or enter your content manually.',
      Component: AddStepDataContentModal,
      ModalProps: {
        size: '95vw',
      },
    })
  }, [props])

  const getDefaultContentTab = useCallback(() => {
    const sourceFile = stepResources?.find((r: any) => r.isSourceFile)
    if (!sourceFile) return 'upload'
    if (sourceFile.type === 'url') return 'website' as ContentTabValue
    if (sourceFile.type === 'text') return 'manual' as ContentTabValue
    return 'upload' as ContentTabValue
  }, [stepResources])

  const updateStep = useCallback(async () => {
    const validate = props.stepForm.validateField

    const validations = [validate('name')]

    if (validations.some(v => v.hasError)) {
      notifications.show({
        title: 'Could Not Submit Form',
        message: 'There was an issue submitting file details. Please check the form for errors.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })

      return
    }

    if (showInApp && !pendingDescription?.trim()) {
      setDescriptionError('File description is required when "Make Available in App" is enabled.')
      notifications.show({
        title: 'Could Not Submit Form',
        message: 'File description is required when "Make Available in App" is enabled.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })

      return
    }

    setDescriptionError(null)

    try {
      // First, validate and save FAQs - this will throw if there are validation errors
      await faqSectionRef.current?.saveUnsavedFAQs()

      const formTitle = props.stepForm.getValues().name

      // Save pending file name change if there is one (for uploaded files only)
      if (pendingFileName && uploadedFileResource) {
        await props.resourceUpdateMutation.mutateAsync({
          resource_id: uploadedFileResource.resourceId,
          step_id: props.stepId,
          file_name: pendingFileName,
          type: uploadedFileResource.type as 'url' | 'image' | 'video' | 'text',
          title: formTitle || uploadedFileResource.title,
          description: uploadedFileResource.description,
          keywords: uploadedFileResource.keywords || [],
          is_approved: true,
        })
      }

      // Save source file resource if title, showInApp, or description changed
      const source = sourceFileResource
      if (source) {
        const hasShowInAppChange = source.showInAppSupported && showInApp !== (source.showInApp ?? false)
        const hasDescriptionChange =
          source.showInAppSupported && pendingDescription !== null && pendingDescription !== (source.description ?? '')
        const hasTitleChange = formTitle && formTitle !== source.title

        if (hasShowInAppChange || hasDescriptionChange || hasTitleChange) {
          await props.resourceUpdateMutation.mutateAsync({
            resource_id: source.resourceId,
            step_id: props.stepId,
            file_name: source.fileName ?? undefined,
            type: source.type as 'url' | 'image' | 'video' | 'text',
            title: formTitle || source.title,
            description: pendingDescription ?? source.description,
            keywords: source.keywords || [],
            show_in_app: showInApp,
            is_approved: true,
          })
        }
      }

      // Only proceed with step update if FAQ validation passed
      await new Promise((resolve, reject) => {
        props.stepUpdateMutation.mutate(
          {
            ...castZodFormToStep(props.stepForm.getValues()),
            step_id: props.stepId!,
          },
          {
            onSuccess: () => {
              props.signalStepSaved?.()
              props.closeModal(() => {
                props.stepForm.setValues(castStepToZodForm())
                props.faqActions.resetStepFAQState()
                setPendingFileName(null)
              })
              resolve(undefined)
            },
            onError: reject,
          },
        )
      })
    } catch (error) {
      console.error('Failed to save step:', error)

      if (error instanceof Error && error.message === 'FAQ_VALIDATION_ERROR') {
        return
      }

      notifications.show({
        title: 'Could Not Save Step',
        message: 'There was an issue saving your step and FAQs. Please try again.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
      return
    }
  }, [
    props,
    uiStore,
    isStepCreating,
    cancelStepCreation,
    cancelContentUpload,
    uploadedFileResource,
    sourceFileResource,
    pendingFileName,
    pendingDescription,
    showInApp,
    faqSectionRef,
    props.stepUpdateMutation,
  ])

  const handleCancel = useCallback(() => {
    props.closeModal(() => {
      const state = uiStore.pendingStepDeletion
      props.stepForm.setValues(castStepToZodForm())
      props.faqActions.resetStepFAQState()
      if (state) {
        props.stepDeleteMutation?.mutate({
          step_id: props.stepId!,
          task_id: props.taskId!,
          showToast: false,
        })
      } else if (isStepCreating) {
        cancelStepCreation()
      } else {
        cancelContentUpload()
      }
      uiStore.setPendingStepDeletion(false)
    })
  }, [props, uiStore, isStepCreating, cancelStepCreation, cancelContentUpload])

  const setTabControlRef = (val: string) => (node: HTMLButtonElement) => {
    tabControlsRefs[val] = node
    setTabControlsRefs(tabControlsRefs)
  }

  // Reset form errors and auto-generation tracking when stepId changes
  useEffect(() => {
    props.stepForm.clearErrors()
    autoGenerationTriggeredRef.current = null
  }, [props.stepId])

  // Pre-fill website URL when editing an existing step created from a URL
  useEffect(() => {
    if (props.stepId && stepResources) {
      const sourceResource = stepResources.find((r: any) => r.type === 'url' && r.isSourceFile)
      const sourceUrl = sourceResource?.data || ''
      setWebsiteUrl(prev => (prev !== sourceUrl ? sourceUrl : prev))
    } else {
      setWebsiteUrl('')
    }
  }, [props.stepId, stepResources])

  // Auto-generate AI suggestions when content is available AND processing is complete
  // Skip API call when step has titleAi – useAIFieldGeneration will seed that instead
  useEffect(() => {
    const isStepCreated = props.cmQuery.isSuccess && props.stepsQuery.isSuccess
    const currentName = props.stepForm.getValues().name
    const shouldTrigger = (props.contentUpdateMutation.isSuccess || isStepCreated) && uploadPhase === 'idle'
    const generationKey = `${props.stepId}-${!!props.stepContentQuery.data}`

    if (
      props.stepId &&
      hasContent &&
      !currentName &&
      shouldTrigger &&
      autoGenerationTriggeredRef.current !== generationKey
    ) {
      autoGenerationTriggeredRef.current = generationKey
      const step = props.stepsQuery.data?.find(s => s.stepId === props.stepId)
      if (step?.titleAi?.trim()) {
        // titleAi will be shown by useAIFieldGeneration seed effect – don't call API
        return
      }
      generateFieldByName('step', 'name')
    }
  }, [
    props.stepId,
    props.stepsQuery.data,
    props.cmQuery.isSuccess,
    props.stepsQuery.isSuccess,
    props.stepCreateMutation.isSuccess,
    props.contentUpdateMutation.isSuccess,
    !!props.stepContentQuery.data,
    uploadPhase,
    generateFieldByName,
    props.stepForm.getValues().name,
    hasContent,
  ])

  // Initialize showInApp and description state from source file resource
  useEffect(() => {
    if (sourceFileResource) {
      setShowInApp(sourceFileResource.showInApp ?? false)
      setPendingDescription(sourceFileResource.description ?? '')
    }
  }, [sourceFileResource?.resourceId, sourceFileResource?.showInApp, sourceFileResource?.description])

  // Auto-generate description when showInApp is enabled and description is empty
  useEffect(() => {
    const isStepCreated = props.cmQuery.isSuccess && props.stepsQuery.isSuccess
    const shouldTrigger =
      (props.contentUpdateMutation.isSuccess || props.stepCreateMutation.isSuccess || isStepCreated) &&
      uploadPhase === 'idle'
    const generationKey = `${props.stepId}-${sourceFileResource?.resourceId}-description`
    const descriptionEmpty = !pendingDescription?.trim()

    if (
      props.stepId &&
      showInApp &&
      descriptionEmpty &&
      shouldTrigger &&
      descriptionAIState === 'idle' &&
      descriptionAutoGenTriggeredRef.current !== generationKey
    ) {
      descriptionAutoGenTriggeredRef.current = generationKey
      generateSourceFileDescription()
    }
  }, [
    props.stepId,
    showInApp,
    pendingDescription,
    descriptionAIState,
    props.contentUpdateMutation.isSuccess,
    props.stepCreateMutation.isSuccess,
    props.cmQuery.isSuccess,
    props.stepsQuery.isSuccess,
    uploadPhase,
    sourceFileResource?.resourceId,
  ])

  useEffect(() => {
    if (!tabInitializedRef.current && stepResources !== undefined) {
      setContentTab(getDefaultContentTab())
      tabInitializedRef.current = true
    }
  }, [stepResources])

  return (
    <Stack bg={theme.colors.gray[0]} gap="xl" pb="xl">
      {/* Content Upload Section */}
      <Paper shadow="sm" p="md" radius="md">
        <Text size="lg" fw={600} mb="md">
          Add Content
        </Text>

        <Tabs variant="none" value={contentTab} onChange={v => v && setContentTab(v as ContentTabValue)}>
          <Tabs.List ref={setTabsRootRef} className={classes.contentTabsList}>
            <Tabs.Tab
              value="upload"
              ref={setTabControlRef('upload')}
              classNames={classes.contentTabClassNames}
              leftSection={<IconUpload size={18} />}
              disabled={contentLoading}>
              Upload File
            </Tabs.Tab>
            <Tabs.Tab
              value="website"
              ref={setTabControlRef('website')}
              classNames={classes.contentTabClassNames}
              leftSection={<IconLink size={18} />}
              disabled={contentLoading}>
              Extract from URL
            </Tabs.Tab>
            <Tabs.Tab
              value="manual"
              ref={setTabControlRef('manual')}
              classNames={classes.contentTabClassNames}
              leftSection={<IconPencil size={18} />}
              disabled={contentLoading}>
              Add Manually
            </Tabs.Tab>

            <FloatingIndicator
              target={contentTab ? tabControlsRefs[contentTab] : null}
              parent={tabsRootRef}
              className={classes.contentTabIndicator}
            />
          </Tabs.List>

          <Tabs.Panel value="upload">
            <Box className={classes.tabPanelWrapper}>
              <Stack gap="md">
                <FileUpload
                  activateOnClick={false}
                  loading={contentLoading}
                  uploadPhase={uploadPhase}
                  uploadProgress={uploadProgress}
                  processProgress={processProgressPct}
                  onDrop={processDroppedFiles}
                  error={props.stepForm.getInputProps('content.file').error}
                  titles={{
                    idle: 'Drag file here to upload',
                    reject: rejectedFileUploadText,
                  }}
                  EmptyStateProps={{
                    TitleProps: {
                      size: 'lg',
                      fw: 700,
                    },
                    gap: 'xs',
                  }}
                  accept={allUploadMimeTypes}
                  maxFiles={1}
                  primaryActionLabel="Select File"
                  actionButtonProps={{
                    variant: 'outline',
                    color: 'gray',
                    size: 'sm',
                  }}
                  hints={{
                    idle: 'Valid file types: png, jpg, tiff, mp4, mov, mp3, wav, m4a, txt, pdf, pptx and docx',
                  }}
                />

                {uploadedFileResource && (
                  <UploadedFilePreview
                    fileName={pendingFileName || uploadedFileResource.fileName || 'Uploaded file'}
                    type={uploadedFileResource.type}
                    resourceKey={uploadedFileResource.resourceKey}
                    resourceData={uploadedFileResource.data}
                    onFileNameChange={setPendingFileName}
                  />
                )}
              </Stack>
            </Box>
          </Tabs.Panel>

          <Tabs.Panel value="website">
            <Box className={classes.tabPanelWrapper}>
              <Stack gap="md">
                <Stack gap="xs">
                  <Text size="sm">Website URL</Text>
                  <Group gap="sm" align="stretch">
                    <TextInput
                      placeholder="https://example.com/article"
                      value={websiteUrl}
                      onChange={e => setWebsiteUrl(e.currentTarget.value)}
                      size="md"
                      disabled={isExtracting || uploadPhase !== 'idle'}
                      leftSection={<IconLink size={18} />}
                      style={{ flex: 1 }}
                      radius="md"
                      variant="filled"
                    />
                    <Button
                      size="md"
                      radius="md"
                      variant="light"
                      onClick={handleExtractFromWebsite}
                      loading={isExtracting}
                      disabled={!websiteUrl.trim() || uploadPhase !== 'idle'}>
                      Extract
                    </Button>
                  </Group>
                </Stack>
                <Paper shadow="none" p="md" radius="sm" bg="gray.0">
                  <Text size="sm" c="dimmed">
                    Enter a URL to automatically extract text content from a webpage. Supports most article pages, blog
                    posts, and documentation.
                  </Text>
                </Paper>
              </Stack>
            </Box>
          </Tabs.Panel>

          <Tabs.Panel value="manual">
            <Box className={classes.tabPanelWrapper}>
              <Stack gap="md">
                <Text size="sm" c="dimmed">
                  Paste or type your content directly
                </Text>
                <Textarea
                  value={props.stepContentQuery.data?.content}
                  size="sm"
                  disabled
                  placeholder="Enter your content here"
                  autosize
                  minRows={6}
                  maxRows={6}
                />

                <Group justify="flex-end">
                  <Button variant="light" onClick={openDataContentModal} disabled={contentLoading}>
                    Open Editor
                  </Button>
                </Group>
              </Stack>
            </Box>
          </Tabs.Panel>
        </Tabs>

        {contentLoading && (
          <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }} mt="sm">
            <Text c="dimmed" className={classes.inputDescription}>
              {uploadPhase === 'uploading' && 'Uploading file...'}
              {uploadPhase === 'processing' && 'Processing content, this may take a few minutes...'}
              {uploadPhase === 'idle' && 'Loading content...'}
            </Text>
          </Box>
        )}
      </Paper>

      {/* File Content Section */}
      <Paper shadow="sm" p="md" radius="md">
        <Text size="lg" fw={600} mb="lg">
          Edit & Configure
        </Text>

        <Stack gap="xxl">
          {/* File Content */}
          <Stack gap="xs">
            <Group justify="space-between">
              <InputLabel label="Extracted Content" withAsterisk />
              <Button
                variant="light"
                size="md"
                radius="md"
                onClick={openDataContentModal}
                disabled={contentLoading || !hasContent}>
                Edit
              </Button>
            </Group>
            <Textarea
              value={props.stepContentQuery.data?.content}
              disabled
              placeholder="Enter your content here"
              autosize
              radius="md"
              variant="filled"
              size="sm"
              minRows={6}
              maxRows={6}
              style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: theme.radius.md }}
            />
          </Stack>

          {/* Title */}
          <AIInputWrapper
            fieldState={getFieldState('name')}
            onAskSidekick={() => generateFieldByName('step', 'name')}
            onAskSidekickWithGuide={instructions =>
              generateFieldByName('step', 'name', { user_instructions: instructions })
            }
            onAccept={() => acceptStepSuggestion('name')}
            onReject={() => rejectSuggestion('name')}
            disabled={!hasContent}
            onRegenerate={() => regenerateFieldByName('step', 'name')}>
            <TextInput
              withAsterisk
              label="Title"
              radius="md"
              disabled={!hasContent}
              classNames={{ input: classes.textInput }}
              {...props.stepForm.getInputProps('name')}
              // {...getRequiredFieldDescriptionProps(props.stepForm, 'name', 'Name of your file.')}
            />
          </AIInputWrapper>

          {/* Make Available in App */}
          <Radio.Card
            component="div"
            radius="md"
            p="md"
            bg={sourceFileResource?.showInAppSupported ? 'gray.0' : 'gray.1'}
            withBorder
            checked={showInApp}
            onClick={() => {
              if (!hasContent) return
              setShowInApp(c => !c)
            }}
            disabled={!sourceFileResource?.showInAppSupported || !hasContent}
            style={{ cursor: sourceFileResource?.showInAppSupported ? 'pointer' : 'not-allowed' }}>
            <Stack gap="md">
              <Group wrap="nowrap" align="flex-start">
                <Radio.Indicator disabled={!hasContent} />
                <Box>
                  <Text fw={500}>Make Available in App</Text>
                  <Text size="sm" c="dimmed">
                    Users will be able to view the source content directly in the app.
                  </Text>
                </Box>
              </Group>

              {showInApp && (
                <Box onClick={e => e.stopPropagation()}>
                  <AIInputWrapper
                    fieldState={{ state: descriptionAIState, suggestion: descriptionSuggestion }}
                    onAskSidekick={generateSourceFileDescription}
                    onAskSidekickWithGuide={instructions => generateSourceFileDescriptionWithGuide(instructions)}
                    onAccept={() => {
                      acceptDescriptionSuggestion()
                      setDescriptionError(null)
                    }}
                    onReject={rejectDescriptionSuggestion}
                    disabled={!hasContent}
                    onRegenerate={generateSourceFileDescription}>
                    <Textarea
                      label="File Description"
                      withAsterisk
                      placeholder="Enter a description for this content"
                      value={pendingDescription ?? ''}
                      onChange={event => {
                        setPendingDescription(event.currentTarget.value)
                        if (descriptionError) setDescriptionError(null)
                      }}
                      error={descriptionError}
                      minRows={3}
                      autosize
                      disabled={!hasContent}
                    />
                  </AIInputWrapper>
                </Box>
              )}
            </Stack>
          </Radio.Card>
        </Stack>
      </Paper>

      {/* FAQs Section */}
      <Paper shadow="sm" p="md" radius="md">
        <Text size="lg" fw={600}>
          FAQs
        </Text>
        <FAQSection {...props} type="step" ref={faqSectionRef} disabled={!hasContent} />
      </Paper>

      {/* Supplemental Resources Section */}
      <Paper shadow="sm" p="md" radius="md">
        <ResourcesTable {...props} type="step" disabled={!hasContent} title="Supplemental Resources" />
      </Paper>

      <Group gap="lg" justify="flex-end">
        <Button
          variant="light"
          color="gray"
          radius="md"
          disabled={isMutating || uploadPhase !== 'idle' || isStepCreating}
          onClick={handleCancel}>
          Cancel
        </Button>

        <Button
          leftSection={<IconDeviceFloppy />}
          onClick={() => {
            updateStep()
            uiStore.setPendingStepDeletion(false)
          }}
          disabled={!hasContent || isMutating || uploadPhase !== 'idle' || isStepCreating}
          loading={isMutating}
          radius="md">
          Save and Exit
        </Button>
      </Group>
    </Stack>
  )
})
